import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Sprout, Building2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { getBrownfieldScan, consumeProject } from '@/lib/api';
import type { ProjectMode, ReconciliationDecision } from '@/lib/api';
import { useNotificationStore } from '@/stores/notification-store';

const PHASE_NAMES = [
  'Intake',
  'Validation',
  'Architecture',
  'Decomposition',
  'Implementation',
  'Verification',
  'Convergence',
  'Deployment',
  'Go-Live',
  'Support',
] as const;

// Mirrors the server-side BUILT_IN_TEMPLATES (project-templates.ts). The consume
// endpoint validates the name, so a stale entry fails loudly rather than silently.
const GREENFIELD_TEMPLATES = [
  { name: '', label: 'Blank — start at Intake' },
  { name: 'Web Application', label: 'Web Application' },
  { name: 'API Service', label: 'API Service' },
  { name: 'Mobile App', label: 'Mobile App' },
] as const;

type BrownfieldScan = Awaited<ReturnType<typeof getBrownfieldScan>>;

function hasDetectedDevelopmentArtifacts(scan: BrownfieldScan): boolean {
  return (
    scan.outcomePackets.length > 0 ||
    scan.adrs.count > 0 ||
    scan.tests.present ||
    scan.hasCodebaseSurvey ||
    scan.hasExistingContext
  );
}

interface ConsumeProjectWizardProps {
  codebaseId: string;
  codebaseName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConsumed?: () => void;
}

export function ConsumeProjectWizard({
  codebaseId,
  codebaseName,
  open,
  onOpenChange,
  onConsumed,
}: ConsumeProjectWizardProps): React.ReactElement {
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<ProjectMode | null>(null);
  const [templateName, setTemplateName] = useState<string>('');
  const [outcomeTitles, setOutcomeTitles] = useState<string[]>([]);
  const [newTitle, setNewTitle] = useState('');
  const [startingPhase, setStartingPhase] = useState<number>(1);
  const [reconciliation, setReconciliation] = useState<ReconciliationDecision[]>([]);
  const [migrateKeys, setMigrateKeys] = useState(true);

  const {
    data: scan,
    isLoading: scanning,
    isError: scanError,
  } = useQuery({
    queryKey: ['brownfield-scan', codebaseId],
    queryFn: () => getBrownfieldScan(codebaseId),
    enabled: open,
  });

  // Seed defaults from the scan once it arrives.
  useEffect(() => {
    if (scan && mode === null) {
      setMode(scan.suggestedMode);
      setStartingPhase(scan.suggestedPhase);
    }
  }, [scan, mode]);

  const consumeMutation = useMutation({
    mutationFn: () =>
      consumeProject(codebaseId, {
        mode: mode ?? 'greenfield',
        templateName: mode === 'greenfield' && templateName ? templateName : undefined,
        importOutcomeTitles: mode === 'brownfield' ? outcomeTitles : undefined,
        startingPhase: mode === 'brownfield' ? startingPhase : undefined,
        reconciliation:
          mode === 'brownfield' && reconciliation.length > 0 ? reconciliation : undefined,
        migrateKeys: scan?.secrets.present ? migrateKeys : undefined,
      }),
    onSuccess: data => {
      void queryClient.invalidateQueries({ queryKey: ['project-mode', codebaseId] });
      void queryClient.invalidateQueries({ queryKey: ['lifecycle-phase', codebaseId] });
      void queryClient.invalidateQueries({ queryKey: ['outcomes', codebaseId] });
      void queryClient.invalidateQueries({ queryKey: ['delivery-status', codebaseId] });
      void queryClient.invalidateQueries({ queryKey: ['recommended-workflows', codebaseId] });
      void queryClient.invalidateQueries({ queryKey: ['secureKeys', codebaseId] });
      // Surface migration problems the user opted into — a systemic failure
      // (migrationError) or per-key failures (failedKeys) leave secrets in .env,
      // so notify visibly rather than only logging.
      const failed = data.failedKeys ?? [];
      if (data.migrationError || failed.length > 0) {
        const message = data.migrationError
          ? data.migrationError
          : `${failed.length} secret key(s) could not be migrated and remain in .env: ${failed
              .map(k => `${k.file}:${k.key}`)
              .join(', ')}`;
        useNotificationStore.getState().addNotification({
          type: 'workflow_failed',
          title: 'Secret migration incomplete',
          message,
        });
      }
      // Operator/infra keys are deliberately NOT stored in the project vault — they
      // belong in MOS HashiCorp Vault. Surface them so the operator routes and removes them.
      const operatorFlagged = data.operatorKeys ?? [];
      if (operatorFlagged.length > 0) {
        useNotificationStore.getState().addNotification({
          type: 'workflow_failed',
          title: 'Operator/infra keys left in .env',
          message: `${operatorFlagged.length} operator/infra key(s) were not stored in the project vault and remain in .env — route them to MOS HashiCorp Vault and remove them: ${operatorFlagged
            .map(k => `${k.file}:${k.key}`)
            .join(', ')}`,
        });
      }
      onConsumed?.();
      resetAndClose();
    },
  });

  function resetAndClose(): void {
    setMode(null);
    setTemplateName('');
    setOutcomeTitles([]);
    setNewTitle('');
    setReconciliation([]);
    setMigrateKeys(true);
    onOpenChange(false);
  }

  function addTitle(): void {
    const t = newTitle.trim();
    if (t) {
      setOutcomeTitles(prev => [...prev, t]);
      setNewTitle('');
    }
  }

  function addReconciliationRow(): void {
    setReconciliation(prev => [...prev, { artifact: '', divergence: '', decision: 'accept' }]);
  }

  function updateReconciliation(i: number, patch: Partial<ReconciliationDecision>): void {
    setReconciliation(prev => prev.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Consume project: {codebaseName}</DialogTitle>
          <DialogDescription>
            Classify this project and seed its lifecycle. Greenfield starts from a blank slate;
            brownfield ingests what already exists.
          </DialogDescription>
        </DialogHeader>

        {scanning ? (
          <div className="text-sm text-text-secondary py-6">Scanning for existing artifacts…</div>
        ) : (
          <div className="space-y-5">
            {scanError && (
              <div className="rounded-md border border-error/40 bg-error/10 p-3 text-sm text-error">
                Failed to scan for existing artifacts — suggested mode and phase are unavailable.
                Pick a mode manually below.
              </div>
            )}

            {/* Detected artifacts summary */}
            {scan && (
              <div className="rounded-md border border-border bg-surface-elevated p-3 text-xs text-text-secondary space-y-1">
                <div className="font-medium text-text-primary">Detected</div>
                <div>
                  {scan.outcomePackets.length} outcome packet
                  {scan.outcomePackets.length === 1 ? '' : 's'} · {scan.adrs.count} ADR/spec file
                  {scan.adrs.count === 1 ? '' : 's'} · tests{' '}
                  {scan.tests.present ? 'present' : 'none'}
                  {scan.hasCodebaseSurvey ? ' · prior survey' : ''}
                  {scan.hasExistingContext ? ' · existing G-Brain context' : ''}
                </div>
                <div>
                  Suggested: <span className="text-text-primary">{scan.suggestedMode}</span>,
                  starting at <span className="text-text-primary">{scan.suggestedPhaseName}</span>
                </div>
                {!hasDetectedDevelopmentArtifacts(scan) && (
                  <div className="pt-1 text-text-tertiary">
                    No lifecycle-ready artifacts were detected yet. Treat this as a starting
                    recommendation, then choose Greenfield if this source is only a baseline or
                    Brownfield if existing work should be reconciled manually.
                  </div>
                )}
              </div>
            )}

            {/* Detected secrets — opt-in migration to the encrypted vault */}
            {scan?.secrets.present && (
              <label className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 p-3 text-xs text-text-secondary cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={migrateKeys}
                  onChange={e => {
                    setMigrateKeys(e.target.checked);
                  }}
                />
                <span>
                  <span className="font-medium text-warning">
                    {scan.secrets.findings.reduce((n, f) => n + f.keys.length, 0)} secret key(s)
                  </span>{' '}
                  detected in {scan.secrets.findings.map(f => f.file).join(', ')}. Move them into
                  the encrypted per-codebase vault and strip them from the .env file(s) during
                  consume.
                </span>
              </label>
            )}

            {/* Step 1 — classify */}
            <div className="space-y-2">
              <div className="text-sm font-medium text-text-primary">Mode</div>
              <div className="grid grid-cols-2 gap-3">
                <ModeCard
                  active={mode === 'greenfield'}
                  icon={<Sprout className="h-4 w-4" />}
                  title="Greenfield"
                  desc="Blank slate — start at Intake or scaffold from a template."
                  onClick={() => {
                    setMode('greenfield');
                  }}
                />
                <ModeCard
                  active={mode === 'brownfield'}
                  icon={<Building2 className="h-4 w-4" />}
                  title="Brownfield"
                  desc="Existing artifacts — import outcomes and reconcile divergences."
                  onClick={() => {
                    setMode('brownfield');
                  }}
                />
              </div>
            </div>

            {/* Step 2a — greenfield */}
            {mode === 'greenfield' && (
              <div className="space-y-2">
                <label
                  htmlFor="consume-starting-template"
                  className="text-sm font-medium text-text-primary"
                >
                  Starting template
                </label>
                <select
                  id="consume-starting-template"
                  value={templateName}
                  onChange={e => {
                    setTemplateName(e.target.value);
                  }}
                  className="min-h-11 w-full rounded-md border border-border bg-surface-elevated px-3 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-ring"
                >
                  {GREENFIELD_TEMPLATES.map(t => (
                    <option key={t.name} value={t.name}>
                      {t.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-text-tertiary">
                  A template seeds draft outcomes and an initial phase. Blank starts empty at
                  Intake.
                </p>
              </div>
            )}

            {/* Step 2b — brownfield */}
            {mode === 'brownfield' && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <label
                    htmlFor="consume-starting-phase"
                    className="text-sm font-medium text-text-primary"
                  >
                    Starting phase
                  </label>
                  <select
                    id="consume-starting-phase"
                    value={startingPhase}
                    onChange={e => {
                      setStartingPhase(Number(e.target.value));
                    }}
                    className="min-h-11 w-full rounded-md border border-border bg-surface-elevated px-3 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-ring"
                  >
                    {PHASE_NAMES.map((name, i) => (
                      <option key={name} value={i + 1}>
                        {i + 1}. {name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <label
                    htmlFor="consume-outcome-title"
                    className="text-sm font-medium text-text-primary"
                  >
                    Outcomes to track
                  </label>
                  {outcomeTitles.length > 0 && (
                    <ul className="space-y-1">
                      {outcomeTitles.map((t, i) => (
                        <li
                          key={`${t}-${String(i)}`}
                          className="flex items-center justify-between rounded border border-border bg-surface px-2 py-1 text-sm text-text-primary"
                        >
                          <span>{t}</span>
                          <button
                            type="button"
                            aria-label={`Remove outcome ${t}`}
                            onClick={() => {
                              setOutcomeTitles(prev => prev.filter((_, idx) => idx !== i));
                            }}
                            className="min-h-8 min-w-8 text-text-tertiary hover:text-error"
                          >
                            <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="flex items-center gap-2">
                    <Input
                      id="consume-outcome-title"
                      value={newTitle}
                      onChange={e => {
                        setNewTitle(e.target.value);
                      }}
                      placeholder="Outcome title from existing work…"
                      className="min-h-11 text-sm"
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addTitle();
                        }
                      }}
                    />
                    <Button
                      variant="outline"
                      onClick={addTitle}
                      disabled={!newTitle.trim()}
                      className="min-h-11 min-w-11"
                      aria-label="Add outcome to track"
                    >
                      <Plus className="h-4 w-4" aria-hidden="true" />
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-text-primary">
                      Reconciliation <span className="text-text-tertiary">(optional)</span>
                    </div>
                    <Button variant="ghost" onClick={addReconciliationRow} className="min-h-11">
                      <Plus className="mr-1 h-3.5 w-3.5" aria-hidden="true" /> Divergence
                    </Button>
                  </div>
                  {reconciliation.map((row, i) => (
                    <div
                      key={i}
                      className="grid grid-cols-1 gap-2 rounded border border-border bg-surface p-2 sm:grid-cols-2"
                    >
                      <Input
                        value={row.artifact}
                        onChange={e => {
                          updateReconciliation(i, { artifact: e.target.value });
                        }}
                        placeholder="Artifact (e.g. docs/adr/0001.md)"
                        aria-label={`Reconciliation ${String(i + 1)} artifact`}
                        className="min-h-11 text-xs"
                      />
                      <Input
                        value={row.divergence}
                        onChange={e => {
                          updateReconciliation(i, { divergence: e.target.value });
                        }}
                        placeholder="What diverges"
                        aria-label={`Reconciliation ${String(i + 1)} divergence`}
                        className="min-h-11 text-xs"
                      />
                      <select
                        value={row.decision}
                        onChange={e => {
                          updateReconciliation(i, { decision: e.target.value });
                        }}
                        aria-label={`Reconciliation ${String(i + 1)} decision`}
                        className="min-h-11 rounded-md border border-border bg-surface-elevated px-2 text-xs text-text-primary"
                      >
                        <option value="accept">accept existing</option>
                        <option value="override">override</option>
                        <option value="merge">merge</option>
                      </select>
                      <Input
                        value={row.notes ?? ''}
                        onChange={e => {
                          updateReconciliation(i, { notes: e.target.value });
                        }}
                        placeholder="Notes"
                        aria-label={`Reconciliation ${String(i + 1)} notes`}
                        className="min-h-11 text-xs"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {consumeMutation.isError && (
              <div className="text-sm text-error">
                {consumeMutation.error instanceof Error
                  ? consumeMutation.error.message
                  : 'Failed to consume project'}
              </div>
            )}

            <div className="flex items-center justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={resetAndClose} className="min-h-11">
                Cancel
              </Button>
              <Button
                onClick={() => {
                  consumeMutation.mutate();
                }}
                disabled={mode === null || consumeMutation.isPending}
                className="min-h-11"
              >
                {consumeMutation.isPending ? 'Consuming…' : 'Consume'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ModeCard({
  active,
  icon,
  title,
  desc,
  onClick,
}: {
  active: boolean;
  icon: React.ReactNode;
  title: string;
  desc: string;
  onClick: () => void;
}): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex flex-col gap-1 rounded-md border p-3 text-left transition-colors ${
        active ? 'border-primary bg-primary/10' : 'border-border bg-surface hover:border-primary/50'
      }`}
    >
      <div className="flex items-center gap-2 text-sm font-medium text-text-primary">
        {icon}
        {title}
        {active && (
          <Badge variant="default" className="ml-auto">
            selected
          </Badge>
        )}
      </div>
      <span className="text-xs text-text-secondary">{desc}</span>
    </button>
  );
}
